import { useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { WhichWeightAPI } from 'WhichWeightAPI'
import { updateUserDetails } from 'redux/slices/user'

import ScreenFrame from 'components/Frame/ScreenFrame'
import ContentFrame from 'components/Frame/ContentFrame'
import LoadingButton from 'components/LoadingButton'

export default function UserProfile() {

    const dispatch = useDispatch()

    const userPicture = useSelector((state) => state.user.picture)
    const userId = useSelector((state) => state.user.id)
    const userEmail = useSelector((state) => state.user.email)
    const userType = useSelector((state) => state.user.type)
    const firstName = useSelector((state) => state.user.firstName)
    const lastName = useSelector((state) => state.user.lastName)

    const [editedFirstName, setEditedFirstName] = useState(firstName || '')
    const [editedLastName, setEditedLastName] = useState(lastName || '')
    const [firstNameValid, setFirstNameValid] = useState(true)
    const [lastNameValid, setLastNameValid] = useState(true)
    const [isSaving, setIsSaving] = useState(false)

    const hasUnsavedChanges = () => {
        return editedFirstName.trim() !== (firstName || '').trim() ||
            editedLastName.trim() !== (lastName || '').trim()
    }

    const SaveProfileDetails = async () => {
        if (editedFirstName.trim().length === 0) {
            setFirstNameValid(false)
            return
        }
        if (editedLastName.trim().length === 0) {
            setLastNameValid(false)
            return
        }

        setFirstNameValid(true)
        setLastNameValid(true)
        setIsSaving(true)

        try {
            const jsonBody = {
                userId: userId,
                editedFirstName: editedFirstName.trim(),
                editedLastName: editedLastName.trim()
            }

            const response = await WhichWeightAPI("CORE", "PATCH", "/account_details_update", jsonBody)

            if (response.data.status === 'success') {
                // Update Redux state
                dispatch(updateUserDetails({
                    editedFirstName: editedFirstName.trim(),
                    editedLastName: editedLastName.trim()
                }))
            } else {
                console.error('Failed to update profile details:', response.data)
            }
        } catch (error) {
            console.error('Error updating profile details:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const HandleDiscardChanges = () => {
        setEditedFirstName(firstName || '')
        setEditedLastName(lastName || '')
        setFirstNameValid(true)
        setLastNameValid(true)
    }

    return (
        <ScreenFrame>
            <div className='max-w-4xl mx-auto py-6 space-y-4'>
                <div className="w-full gap-4 flex items-center pb-4">
                    <div>
                        <img className="h-12 w-12 rounded-full shadow-md" src={userPicture} alt="Profile Pic" />
                    </div>

                    <div className='mb-0.5 flex-grow'>
                        <h1 className="text-xl font-semibold text-gray-900 tracking-tight flex-grow">
                            {firstName} {lastName}
                        </h1>

                        <p className="text-sm">{(userEmail && userEmail.length) > 0 ? userEmail : 'No Email'}</p>
                    </div>
                </div>

                <ContentFrame>
                    <div className="w-full p-6 space-y-6">
                        <div>
                            <label htmlFor="first-name" className="block text-sm font-medium leading-6 text-gray-900">
                                First name*
                            </label>
                            <div className="mt-2">
                                <input
                                    id="first-name"
                                    name="first-name"
                                    type="text"
                                    placeholder="Enter first name here"
                                    autoComplete="given-name"
                                    // className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                                    value={editedFirstName}
                                    onChange={(e) => {
                                        setEditedFirstName(e.target.value)
                                        if (e.target.value.trim().length > 0) {
                                            setFirstNameValid(true)
                                        }
                                    }}
                                    maxLength={255}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="last-name" className="block text-sm font-medium leading-6 text-gray-900">
                                Last name*
                            </label>
                            <div className="mt-2">
                                <input
                                    id="last-name"
                                    name="last-name"
                                    type="text"
                                    placeholder="Enter last name here"
                                    autoComplete="family-name"
                                    // className="block w-full rounded-lg border-0 px-3 py-2.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 sm:text-sm sm:leading-6 focus:outline-orange-400"
                                    className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                                    value={editedLastName}
                                    onChange={(e) => {
                                        setEditedLastName(e.target.value)
                                        if (e.target.value.trim().length > 0) {
                                            setLastNameValid(true)
                                        }
                                    }}
                                    maxLength={255}
                                    disabled={true}
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                                Email
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={userEmail || ''}
                                disabled
                                className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                            />
                            {/* <p className="mt-1.5 text-xs text-gray-500">Reach out to support@kinesisintegrated.com to change your email</p> */}
                        </div>

                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1.5">
                                Role
                            </label>
                            <input
                                id="email"
                                type="email"
                                value={userType.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                                disabled
                                className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5 text-sm text-gray-500 cursor-not-allowed"
                            />
                            <p className="mt-1.5 text-xs text-gray-500">Reach out to your organization admin to change your role. More details can be found on the 'Coaches' page.</p>
                        </div>

                        {/* Action Buttons */}
                        {hasUnsavedChanges() && (
                            <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-100">
                                <button
                                    onClick={HandleDiscardChanges}
                                    className="px-4 py-2 border border-gray-200 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Discard
                                </button>
                                <LoadingButton
                                    buttonText="Save Changes"
                                    isLoading={isSaving}
                                    action={SaveProfileDetails}
                                    className="inline-flex justify-center items-center gap-x-2 bg-orange-500 rounded-md text-white text-sm font-medium py-2 px-4 hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
                                />
                            </div>
                        )}
                    </div>
                </ContentFrame>

                <p className='pt-1 text-center text-gray-600 text-sm'>
                    To edit profile, contact us at <a href="mailto:support@kinesisintegrated.com" className="text-blue-500 underline text-sm">support@kinesisintegrated.com</a>
                </p>
            </div>
        </ScreenFrame>
    )
}
